// Preview wrapper — the BattleScreen on real, populated Grids without
// Firebase, sign-in, or a paired opponent.
//
//   ?preview          the archived war's Grids on the board
//   ?preview&frost    SUBMITTED frost up      ?preview&ready   both frosted
//   ?preview&reveal   plays a scripted REVEAL on load (a 3-shot strike that
//                     kills an opp structure, a 3-on-3 wash, an opp strike on
//                     your Grid, then recon planes) — the hook's clash props
//                     and Grid swaps, on the hook's own timing
//   ?preview&resupply a drop zone's delivery lands in corridor 5 on load —
//                     the crate launches from the mature Base in corridor 3
//   ?preview&peek     the eye held open on scripted Hives, both sides (hex/peekDemo):
//                     A unfound, B with three bearings, C struck,
//                     D killed. ?preview&peek=hold puts the eye back on your
//                     finger
//   ?preview&replay   plays the OPPONENT'S ROUND REPLAY on load — their
//                     previous board holds, then (as useGameState.playOppReplay
//                     steps it) corridor 1's front drone hops into corridor 2
//                     (landing at its front, though a deploy into corridor 2
//                     was ordered first), corridor 3's pair flies down to build
//                     a Base, and three fresh deploys glide in from their rear
//                     edge (one behind the arrival in corridor 2) — landing on
//                     their submitted board
//   ?preview&clock    a sync war's chess clocks ticking: yours running with
//                     time to spare, theirs paused. ?preview&clock=low starts
//                     yours just over a minute from running out
//
// Grid assets run on local state with the hook's rules: the dock's buy tabs
// (a yield of 300 to spend), tap-to-place rockets and recon planes, plane
// aiming, BUILD BASE from a two-veteran corridor's menu, and their undos.
//
// Data: src/config/hexReviewFixture.json — the final round of an archived
// real war (both Grids post-clash, both boards, war capacities), player
// identities stripped. Corridor 4 starts attacking so the aim picker is live
// tap an opp cell in that corridor to set the aim. Board actions run on
// local state with the hook's rules. A few corridors are stacked with drones
// on both sides (beyond what the archived round held) so the token stacking
// can be judged, and a MATURE BASE stands in corridor 3 of your Grid (the
// first clear row) so the drop zone it owes wears its tether while placed
// and the ?resupply crate has a Base to launch from.

import { useState, useEffect, useRef } from 'react'
import BattleScreen from './BattleScreen'
import WarClock from '../prototype/WarClock'
import fixture from '../../config/hexReviewFixture.json'
import { migrateBroods } from '../../rules/subplotGrid'
import { buildPeekDemoHive } from './hex/peekDemo'
import {
    deployArmy, undoDeploy, moveUnit, undoMove, buildFort, undoBuildFort, canPlaceDropZone, fittingDropZones, requiredDropZones, applyResupplyDeliveries,
    dropZoneSources, ensureSlots, matureCorridor, buildRoundActionsLedger, buildOppReplaySteps,
} from '../../rules/gameLogic'
import { resolveCrater } from '../../rules/subplotRockets'
import { canPlaceBase, baseFootprint, addBrood, matureBroodAt } from '../../rules/subplotBases'
import { ROCKET_COST, DRONE_COST } from '../../rules/gameConfig'
import { applyDroneScan } from '../../rules/subplotSurveillance'
import { plotOf, subIndex, cloneHomefront } from '../../rules/subplotGrid'

import {
    missileImpactMs, missileFlightTotalMs, MISSILE_SURPLUS_STAGGER_MS, DRONE_FLIGHT_MS, DRONE_HOVER_MS,
    REPLAY_LEAD_MS, BASE_BUILD_WIGGLE_MS, BASE_BUILD_FLIGHT_MS,
} from '../../rules/gameConfig'

// The fixture predates rules v8: its Broods still sit among the structures.
migrateBroods(fixture.self.hf)
migrateBroods(fixture.opp.hf)

// Overwrite a corridor's drone counts; ensureSlots rebuilds the tokens from
// the counts (front → back: veterans, moved-in, then new arrivals).
const stack = (bf, { vets = 0, moved = 0, greens = 0, attacking = false }) => ({
    ...bf,
    armyQty: vets, movedInArmies: moved, newArmies: greens,
    slots: Array(bf.maxArmyQty).fill(null),
    isAttacking: attacking,
})

const SELF_STACKS = {
    0: { vets: 3 },                       // corridor 1: three veterans deep
    2: { vets: 2, greens: 1 },            // corridor 3: two vets + a new arrival
    3: { vets: 2, attacking: true },      // corridor 4: attacking pair (aim picker live)
}
const OPP_STACKS = {
    0: { vets: 3 },                       // corridor 1
    2: { vets: 2 },                       // corridor 3
    5: { vets: 2 },                       // corridor 6 (capacity 2 — full)
}

// Your mature Base: corridor 3's first clear row, on the Grid and in the
// corridor's ledger both (fortCount is what earns the drop zone).
const SELF_BASE_COL = 2
function withMatureBase(hf, board) {
    const h = cloneHomefront(hf)
    let centerS = null
    for (let row = 1; row < 14 && centerS == null; row++) {
        const s = subIndex(SELF_BASE_COL * 3 + 1, row)
        if (canPlaceBase(h, SELF_BASE_COL, s)) centerS = s
    }
    if (centerS == null) return { hf, board }
    addBrood(h, SELF_BASE_COL, centerS, fixture.source.round)
    matureBroodAt(h, SELF_BASE_COL, centerS)
    const b = board.map((bf, i) => (i === SELF_BASE_COL
        ? { ...bf, fortCount: 1, fortInConstruction: 0, bases: [{ centerS, progress: 2, mature: true, hp: 2, egg: 'egg' }] }
        : bf))
    return { hf: h, board: b }
}
const PEEK_DEMO = new URLSearchParams(window.location.search).get('peek')   // null, '' (held open) or 'hold'
// ?peek: both Hives generated with today's structure shapes (the fixture's
// are older square-era shapes the peek's crossing solver can't fit).
const PEEK_SELF_SEED = 12
const SELF_FIX = withMatureBase(PEEK_DEMO != null ? buildPeekDemoHive(PEEK_SELF_SEED) : fixture.self.hf,
    fixture.self.board.map((bf, i) => (SELF_STACKS[i] ? stack(bf, SELF_STACKS[i]) : bf)))
const SELF_BOARD = SELF_FIX.board
const SELF_HF = SELF_FIX.hf
const OPP_BOARD  = fixture.opp.board.map((bf, i)  => (OPP_STACKS[i]  ? stack(bf, OPP_STACKS[i])  : bf))
const REVEAL_DEMO = new URLSearchParams(window.location.search).has('reveal')
const RESUPPLY_DEMO = new URLSearchParams(window.location.search).has('resupply')
const REPLAY_DEMO = new URLSearchParams(window.location.search).has('replay')
const CLOCK_DEMO = new URLSearchParams(window.location.search).get('clock')     // null, '' or 'low'
const CLOCK_DEMO_START = Date.now()
const CLOCK_DEMO_WAR = {
    clock: {
        p1: { leftMs: CLOCK_DEMO === 'low' ? 64000 : 283000, runningSince: CLOCK_DEMO_START },
        p2: { leftMs: 196000, runningSince: null },
    },
}
const YIELD = 300

// A live structure cell in corridor `col` of a Grid (the middle cell of a bar
// when there is one), for a strike that scores a kill.
function liveStructureCell(hf, col) {
    for (const st of Object.values(hf.structures ?? {})) {
        const fp = st.footprint ?? []
        if (!fp.length || fp.some(f => hf.subplots[f]?.destroyed)) continue
        if (plotOf(fp[0]).col !== col) continue
        return fp[Math.floor((fp.length - 1) / 2)]
    }
    return null
}

// The opponent's round for ?replay, played the way their client plays it:
// previous board → their planning ops → the actions ledger captured before
// maturation → the matured board they submit (see gameLogic's playRound
// test helper). A deploy into corridor 2 and THEN a move out of corridor 1
// into it (the replay shows the hop first, landing at the front, and the
// green glides in behind it), corridor 3's two veterans building a Base on
// the first clear row of their Grid, and two more deploys into corridor 5.
function oppRound(fromBoard, oppHf) {
    let b = fromBoard.map(bf => ensureSlots(bf))
    b = deployArmy(b, b[1].id, 3).boardArr
    b = moveUnit(b, b[0].id, 'right').boardArr
    let centerS = null
    for (let row = 1; row < 14 && centerS == null; row++) {
        const s = subIndex(2 * 3 + 1, row)          // corridor 3's middle column
        if (canPlaceBase(oppHf, 2, s)) centerS = s
    }
    if (centerS != null) b = buildFort(b, b[2].id, centerS).boardArr
    b = deployArmy(b, b[4].id, 2).boardArr
    b = deployArmy(b, b[4].id, 1).boardArr
    return { ledger: buildRoundActionsLedger(b), matured: b.map(matureCorridor), centerS }
}

export default function BattleScreenPreview() {
    const [clockNow, setClockNow] = useState(CLOCK_DEMO_START)
    useEffect(() => {
        if (CLOCK_DEMO == null) return undefined
        const t = setInterval(() => setClockNow(Date.now()), 1000)
        return () => clearInterval(t)
    }, [])
    const [selfBoard, setSelfBoard] = useState(SELF_BOARD)
    const [armiesToDeploy, setArmiesToDeploy] = useState(1)
    const [dropZones, setDropZones] = useState([])
    const [message, setMessage] = useState(null)
    const [targetSubplots, setTargetSubplots] = useState(() => Array(7).fill(null))
    const onTargetPlot = (col, s) => {
        console.log('aim:', col, s)
        setTargetSubplots(prev => prev.map((v, i) => (i === col ? s : v)))
    }
    // Grid assets — useGameState's onHomefrontAction / onDroneTarget, in
    // miniature (same guards, local state).
    const [pendingRockets, setPendingRockets] = useState([])
    const [pendingDrones, setPendingDrones] = useState([])
    const pendingSpend = pendingRockets.length * ROCKET_COST + pendingDrones.length * DRONE_COST
    const spendable = YIELD - pendingSpend
    const onHomefrontAction = (subS, action) => {
        console.log('homefront:', subS, action)
        const cell = selfHf?.subplots?.[subS]
        const st = cell?.structureId != null ? selfHf.structures?.[cell.structureId] : null
        if (!st || cell.destroyed) return
        const hasDeployedRocket = (st.rocketCells || []).includes(subS)
        const hasPendingDrone = pendingDrones.some(d => d.launchS === subS)
        if (action === 'buyRocket') {
            if (hasDeployedRocket || pendingRockets.includes(subS)) { setMessage('That subplot already has a rocket.'); return }
            if (hasPendingDrone) { setMessage('That subplot is launching a recon plane this round.'); return }
            if (spendable < ROCKET_COST) { setMessage(`Need ${ROCKET_COST} yield to buy a rocket (have ${spendable}).`); return }
            setPendingRockets(prev => [...prev, subS])
        } else if (action === 'undoRocket') {
            setPendingRockets(prev => { const i = prev.lastIndexOf(subS); if (i === -1) return prev; const n = [...prev]; n.splice(i, 1); return n })
        } else if (action === 'buyDrone') {
            if (hasDeployedRocket || pendingRockets.includes(subS)) { setMessage('That subplot already hosts a rocket.'); return }
            if (hasPendingDrone) { setMessage('That subplot is already launching a recon plane.'); return }
            if (spendable < DRONE_COST) { setMessage(`Need ${DRONE_COST} yield to buy a recon plane (have ${spendable}).`); return }
            setPendingDrones(prev => [...prev, { launchS: subS, targetS: null }])
        } else if (action === 'undoDrone') {
            setPendingDrones(prev => { const i = prev.map(d => d.launchS).lastIndexOf(subS); if (i === -1) return prev; const n = [...prev]; n.splice(i, 1); return n })
        }
    }
    const onDroneTarget = (col, subS) => {
        console.log('aim plane:', col, subS)
        if (plotOf(subS).col !== col) return
        setPendingDrones(prev => {
            const inCol = (d) => plotOf(d.launchS).col === col
            let idx = prev.findIndex(d => inCol(d) && d.targetS == null)
            if (idx === -1) for (let i = prev.length - 1; i >= 0; i--) if (inCol(prev[i])) { idx = i; break }
            if (idx === -1) return prev
            const next = [...prev]
            next[idx] = { ...next[idx], targetS: subS }
            return next
        })
    }

    // The hook's dispatcher, in miniature: the same gameLogic rules, local state.
    const onAction = (bfId, action, extras) => {
        console.log('action:', bfId, action, extras)
        const col = selfBoard.findIndex(bf => bf.id === bfId)
        switch (action) {
            case 'buildFort': {
                const centerS = extras?.centerS ?? null
                const pendingCells = new Set((selfBoard[col]?.pendingBaseBuilds ?? []).flatMap(p => baseFootprint(p.centerS) ?? []))
                if (col === -1 || !canPlaceBase(selfHf, col, centerS) || (baseFootprint(centerS) ?? []).some(c => pendingCells.has(c))) {
                    setMessage('Pick a clear spot in this corridor for your Base.'); return
                }
                const r = buildFort(selfBoard, bfId, centerS, extras?.uids ?? null)
                if (!r.success) { setMessage(r.reason); return }
                setSelfBoard(r.boardArr)
                break
            }
            case 'undoBuildFort': {
                const r = undoBuildFort(selfBoard, bfId, extras?.centerS ?? null)
                if (!r.success) { setMessage(r.reason); return }
                setSelfBoard(r.boardArr)
                break
            }
            case 'attack':
                setSelfBoard(prev => prev.map(bf => (bf.id === bfId ? { ...bf, isAttacking: true } : bf)))
                break
            case 'undoAttack':
                setSelfBoard(prev => prev.map(bf => (bf.id === bfId ? { ...bf, isAttacking: false } : bf)))
                if (col >= 0) setTargetSubplots(prev => prev.map((v, i) => (i === col ? null : v)))
                break
            case 'deploy': {
                const r = deployArmy(selfBoard, bfId, armiesToDeploy)
                if (!r.success) { setMessage(r.reason); return }
                setSelfBoard(r.boardArr); setArmiesToDeploy(r.armiesToDeploy)
                break
            }
            case 'remove': {
                const r = undoDeploy(selfBoard, bfId, armiesToDeploy)
                if (!r.success) { setMessage(r.reason); return }
                setSelfBoard(r.boardArr); setArmiesToDeploy(r.armiesToDeploy)
                break
            }
            case 'move': {
                const r = moveUnit(selfBoard, bfId, extras?.direction, extras?.uid ?? null)
                if (!r.success) { setMessage(r.reason); return }
                setSelfBoard(r.boardArr)
                break
            }
            case 'undoMove': {
                const r = undoMove(selfBoard, bfId)
                if (!r.success) { setMessage(r.reason); return }
                setSelfBoard(r.boardArr)
                break
            }
            default:
                break
        }
    }
    const requiredDropZoneCount = Math.max(1, requiredDropZones(selfBoard))
    const onDropZone = (col, action) => {
        console.log('dropZone:', col, action)
        if (action === 'place') {
            if (dropZones.length >= requiredDropZoneCount) return
            const { valid, reason } = canPlaceDropZone(selfBoard, col, dropZones)
            if (!valid) { setMessage(reason); return }
            setDropZones(prev => [...prev, col])
        } else if (action === 'remove') {
            setDropZones(prev => { const i = prev.lastIndexOf(col); if (i === -1) return prev; const n = [...prev]; n.splice(i, 1); return n })
        }
    }
    const placingDropZones = armiesToDeploy === 0 && dropZones.length < requiredDropZoneCount
    // As the game: a drone moved into a placed zone's slot sends the zone back.
    useEffect(() => {
        if (!dropZones.length) return
        const kept = fittingDropZones(selfBoard, dropZones)
        if (kept === dropZones) return
        setDropZones(kept)
        setMessage('A drone took a drop zone\'s slot. Place it again.')
    }, [selfBoard]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Resupply arrival (?resupply) ───────────────────────────────────────
    // A drop zone declared in corridor 5 last round delivers a drone there.
    const [resupplyDeliveries, setResupplyDeliveries] = useState(null)
    useEffect(() => {
        if (!RESUPPLY_DEMO) return undefined
        // (StrictMode's mount / unmount / remount clears and re-arms the timer.)
        const t = setTimeout(() => {
            setSelfBoard(prev => {
                const r = applyResupplyDeliveries(prev, [4], dropZoneSources(prev, [4]))
                setResupplyDeliveries({ self: r.delivered, opp: [], sources: { self: r.sources, opp: [] } })
                return r.boardArr
            })
        }, 1500)
        return () => clearTimeout(t)
    }, [])

    // ── Scripted reveal (?reveal) ──────────────────────────────────────────
    const [selfHf, setSelfHf] = useState(SELF_HF)
    const [oppHf, setOppHf] = useState(() => (PEEK_DEMO != null ? buildPeekDemoHive() : fixture.opp.hf))
    const [anim, setAnim] = useState({ isAnimating: false })
    const [baseBuildFx, setBaseBuildFx] = useState(null)
    const [lost, setLost] = useState({ self: 0, opp: 0 })
    const [droneFlights, setDroneFlights] = useState(null)
    // StrictMode mounts, unmounts and remounts in development: the alive flag
    // is re-armed on every mount (a ref keeps its value across the fake
    // unmount), and the script itself starts only once.
    const aliveRef = useRef(true)
    const startedRef = useRef(false)
    useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false } }, [])
    // ── Scripted opponent replay (?replay) ─────────────────────────────────
    // playOppReplay's loop, in miniature: hold the previous board, then step
    // through buildOppReplaySteps' boards on its clock — the build beat fires
    // the descent motes a frame before its board removes the funding tiles
    // and merges the construction onto their Grid as the motes land.
    const replayStartedRef = useRef(false)
    useEffect(() => {
        if (!REPLAY_DEMO || replayStartedRef.current) return undefined
        replayStartedRef.current = true
        const delay = ms => new Promise(res => setTimeout(res, ms))
        const clear = { activeClashId: null, clashResult: null, activeClashContext: null, activeClashWinner: null,
            activeClashSelfStrength: null, activeClashOppStrength: null, activeClashOppTargetIndex: null,
            activeClashSelfTargetIndex: null, activeClashAbsorbed: 0, activeClashAimS: null }
        const from = OPP_BOARD.map(bf => ensureSlots(bf))
        const { ledger, matured, centerS } = oppRound(from, fixture.opp.hf)
        const steps = buildOppReplaySteps(from, matured, ledger)
        ;(async () => {
          try {
            if (!steps) { console.warn('replay demo: no steps'); return }
            setAnim({ isAnimating: false, ...clear, oppBoard: from })
            await delay(1500)
            if (!aliveRef.current) return
            setAnim({ isAnimating: true, ...clear, oppBoard: from })
            await delay(REPLAY_LEAD_MS)
            for (const step of steps) {
                if (!aliveRef.current) return
                if (step.buildCol != null && centerS != null) {
                    setBaseBuildFx({ col: step.buildCol, builds: [{ centerS, uids: step.buildPairs?.[0] ?? [] }], key: Date.now() })
                    await delay(50)
                    setAnim(a => ({ ...a, oppBoard: step.board }))
                    const preLand = Math.max(0, Math.min(step.holdMs, BASE_BUILD_WIGGLE_MS + BASE_BUILD_FLIGHT_MS - 130) - 50)
                    await delay(preLand)
                    setOppHf(hf => { const h = cloneHomefront(hf); addBrood(h, step.buildCol, centerS, fixture.source.round + 1); return h })
                    await delay(Math.max(0, step.holdMs - preLand - 50))
                } else {
                    setAnim(a => ({ ...a, oppBoard: step.board }))
                    await delay(step.holdMs)
                }
            }
            setBaseBuildFx(null)
            setAnim({ isAnimating: false, ...clear, oppBoard: matured })
          } catch (err) {
            console.error('replay demo failed:', err)
          }
        })()
        return undefined
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!REVEAL_DEMO || startedRef.current) return undefined
        startedRef.current = true
        const delay = ms => new Promise(res => setTimeout(res, ms))
        const clear = { activeClashId: null, clashResult: null, activeClashContext: null, activeClashWinner: null,
            activeClashSelfStrength: null, activeClashOppStrength: null, activeClashOppTargetIndex: null,
            activeClashSelfTargetIndex: null, activeClashAbsorbed: 0, activeClashAimS: null }
        const board = (b, col, patch) => b.map((bf, i) => (i === col ? { ...bf, ...patch } : bf))
        ;(async () => {
          try {
            await delay(1200)
            if (!aliveRef.current) return
            setAnim({ isAnimating: true, ...clear })
            // ── Clash 1: corridor 2, you attack 3 vs 0 → a 3-shot strike on a
            //    live opp structure (margin 3 = the 19-cell hexagon): a kill,
            //    whose silhouette flies to the kill bar.
            const KILL_COL = 1
            const aimA = liveStructureCell(fixture.opp.hf, KILL_COL) ?? subIndex(4, 4)
            const selfA = board(SELF_BOARD, KILL_COL, { armyQty: 3, newArmies: 0, movedInArmies: 0, isAttacking: true, slots: Array(SELF_BOARD[KILL_COL].maxArmyQty).fill(null) })
            const oppA = board(OPP_BOARD, KILL_COL, { armyQty: 0, slots: Array(OPP_BOARD[KILL_COL].maxArmyQty).fill(null) })
            setSelfBoard(selfA)
            // Its aim was declared: the locked crosshairs ride the duel and
            // fade off as the first shot lands.
            setTargetSubplots(prev => prev.map((v, i) => (i === KILL_COL ? aimA : v)))
            const rA = resolveCrater(cloneHomefront(fixture.opp.hf), aimA, 3, { seed: 1 })
            setAnim({ isAnimating: true, ...clear, activeClashId: selfA[KILL_COL].id, activeClashContext: 'self_attacked', activeClashWinner: 'self',
                activeClashSelfStrength: 3, activeClashOppStrength: 0, activeClashSelfTargetIndex: plotOf(aimA).row, activeClashAimS: aimA, oppBoard: oppA })
            const impactA = missileImpactMs(0, 0)
            rA.stageHomefronts.forEach((hf, i) => setTimeout(() => aliveRef.current && setOppHf(hf), impactA + i * MISSILE_SURPLUS_STAGGER_MS))
            setTimeout(() => aliveRef.current && setOppHf(rA.homefront), impactA + rA.stageHomefronts.length * MISSILE_SURPLUS_STAGGER_MS)
            await delay(Math.max(3000, missileFlightTotalMs(3, 0, true) + 450))
            if (!aliveRef.current) return
            setAnim(a => ({ ...a, clashResult: 'WIN' }))
            await delay(1680)
            if (!aliveRef.current) return
            setSelfBoard(b => board(b, KILL_COL, { armyQty: 0, isAttacking: false, slots: Array(b[KILL_COL].maxArmyQty).fill(null) }))
            // ── Clash 2: corridor 1, 3 on 3 — a wash.
            setAnim({ isAnimating: true, ...clear, activeClashId: SELF_BOARD[0].id, activeClashContext: 'both', activeClashWinner: 'tie',
                activeClashSelfStrength: 3, activeClashOppStrength: 3, oppBoard: oppA })
            await delay(Math.max(1800, missileFlightTotalMs(3, 3, false) + 450))
            if (!aliveRef.current) return
            setAnim(a => ({ ...a, clashResult: 'TIE' }))
            await delay(1680)
            if (!aliveRef.current) return
            setSelfBoard(b => board(b, 0, { armyQty: 0, slots: Array(b[0].maxArmyQty).fill(null) }))
            // ── Clash 3: corridor 6, the opponent's 2 vs your 0 → a 2-shot strike
            //    on your Grid (margin 2 = 13 cells).
            const aimC = subIndex(16, 4)
            const rC = resolveCrater(cloneHomefront(SELF_HF), aimC, 2, { seed: 2 })
            const oppC = board(oppA, 0, { armyQty: 0, slots: Array(oppA[0].maxArmyQty).fill(null) })
            setAnim({ isAnimating: true, ...clear, activeClashId: SELF_BOARD[5].id, activeClashContext: 'opp_attacked', activeClashWinner: 'opp',
                activeClashSelfStrength: 0, activeClashOppStrength: 2, activeClashOppTargetIndex: plotOf(aimC).row, activeClashAimS: aimC, oppBoard: oppC })
            const impactC = missileImpactMs(0, 0)
            rC.stageHomefronts.forEach((hf, i) => setTimeout(() => aliveRef.current && setSelfHf(hf), impactC + i * MISSILE_SURPLUS_STAGGER_MS))
            setTimeout(() => aliveRef.current && setSelfHf(rC.homefront), impactC + rC.stageHomefronts.length * MISSILE_SURPLUS_STAGGER_MS)
            await delay(Math.max(1800, missileFlightTotalMs(0, 2, true) + 450))
            if (!aliveRef.current) return
            setAnim(a => ({ ...a, clashResult: 'LOSS' }))
            await delay(1680)
            if (!aliveRef.current) return
            // ── Recon planes: yours from a structure in corridor 2 to the opp
            //    field; theirs down corridor 5 onto your Grid.
            const launch = liveStructureCell(SELF_HF, 1) ?? subIndex(4, 6)
            const flights = { self: [{ launchS: launch, targetS: subIndex(4, 3) }], opp: [{ targetS: subIndex(13, 5) }] }
            setAnim({ isAnimating: true, ...clear, oppBoard: board(oppC, 5, { armyQty: 0, slots: Array(oppC[5].maxArmyQty).fill(null) }) })
            setDroneFlights(flights)
            await delay(DRONE_FLIGHT_MS)
            if (!aliveRef.current) return
            setOppHf(hf => applyDroneScan(hf, flights.self[0].targetS).homefront)
            setSelfHf(hf => applyDroneScan(hf, flights.opp[0].targetS).homefront)
            await delay(DRONE_HOVER_MS)
            if (!aliveRef.current) return
            setDroneFlights(null)
            setAnim(a => ({ ...a, isAnimating: false, ...clear }))
          } catch (err) {
            console.error('reveal demo failed:', err)
          }
        })()
        return undefined
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <BattleScreen
            headerClock={CLOCK_DEMO != null ? <WarClock war={CLOCK_DEMO_WAR} seat="p1" now={clockNow} /> : null}
            selfScore={3}
            oppScore={2}
            currentRound={fixture.source.round + 1}
            isPaired={true}
            selfBoard={selfBoard}
            oppBoard={anim.oppBoard ?? OPP_BOARD}
            selfHf={selfHf}
            oppHf={oppHf}
            selfLost={lost.self}
            oppLost={lost.opp}
            onStructureKilled={(side) => setLost(l => ({ ...l, [side]: l[side] + 1 }))}
            isAnimating={anim.isAnimating}
            activeClashId={anim.activeClashId ?? null}
            clashResult={anim.clashResult ?? null}
            activeClashContext={anim.activeClashContext ?? null}
            activeClashWinner={anim.activeClashWinner ?? null}
            activeClashSelfStrength={anim.activeClashSelfStrength ?? null}
            activeClashOppStrength={anim.activeClashOppStrength ?? null}
            activeClashOppTargetIndex={anim.activeClashOppTargetIndex ?? null}
            activeClashSelfTargetIndex={anim.activeClashSelfTargetIndex ?? null}
            activeClashAbsorbed={anim.activeClashAbsorbed ?? 0}
            activeClashAimS={anim.activeClashAimS ?? null}
            droneFlights={droneFlights}
            baseBuildFx={baseBuildFx}
            warCapacities={fixture.warCapacities}
            targetSubplots={targetSubplots}
            targetPlots={targetSubplots.map(s => (s == null ? null : Math.floor((s % 15) / 3)))}
            onTargetPlot={onTargetPlot}
            canDeploy={armiesToDeploy > 0}
            yieldBalance={spendable}
            canAffordRocket={spendable >= ROCKET_COST}
            canAffordDrone={spendable >= DRONE_COST}
            onHomefrontAction={onHomefrontAction}
            pendingRockets={pendingRockets}
            pendingDrones={pendingDrones}
            onDroneTarget={onDroneTarget}
            resupplyDeliveries={resupplyDeliveries}
            armiesToDeploy={armiesToDeploy}
            requiredDropZoneCount={requiredDropZoneCount}
            dropZones={dropZones}
            onDropZone={onDropZone}
            placingDropZones={placingDropZones}
            message={message}
            setMessage={setMessage}
            isSubmitDisabled={window.location.search.includes('frost')}   // frosted = this round is in
            onAction={onAction}
            onSubmit={() => console.log('submit')}
            revealOverlayUp={window.location.search.includes('frost')}
            oppReadyOverlayUp={window.location.search.includes('ready')}
            peekHeld={PEEK_DEMO === ''}
        />
    )
}
